import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  Box,
  Link,
  List,
  ListItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material/";

import t__ from "../../util/lang/util";
import countryDrawer from "../../util/lang/countryDrawer";

const ATM_COVERAGE_FOOTNOTE_ID = "atm-coverage-footnote";

const PREFIX = "AtmBox";

const classes = {
  orangeList: `${PREFIX}-orangeList`,
  longList: `${PREFIX}-longList`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.orangeList}`]: {
    "& span": {
      backgroundColor: theme.palette.primary.light,
      height: "5px",
      width: "5px",
      borderRadius: "5px",
      position: "absolute",
      left: "0px",
      top: 12,
      fontSize: "10px",
    },
  },

  [`& .${classes.longList}`]: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "start",
    "& li": {
      width: "50%",
    },
  },
}));

const AtmBox = ({ countryData, inDrawer, fixedFontSize }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const mdScreen = useMediaQuery(theme.breakpoints.up("md"));
  const hasAtmBanks = countryData.atm_banks[0] !== "";
  const noCCCoverage = countryData.credit_level === "NO COVERAGE";

  return (
    <StyledBox
      bgcolor="background.light"
      borderRadius={theme.spacing(3)}
      padding={
        inDrawer
          ? theme.spacing(5)
          : mdScreen
            ? theme.spacing(10)
            : theme.spacing(5)
      }
      width="100%"
    >
      <Box mb={3}>
        <Typography variant="body1" sx={{ fontSize: "1rem" }}>
          <b>
            {t__(
              theme.lang,
              countryDrawer,
              `atm.${
                hasAtmBanks
                  ? noCCCoverage
                    ? "coverageAlt"
                    : "coverage"
                  : "noCoverage"
              }.headline`
            )}
          </b>
          <Box component="sup">
            <Link
              href={`#${ATM_COVERAGE_FOOTNOTE_ID}`}
              underline="none"
              color="inherit"
              onClick={(event) => {
                const target = document.getElementById(
                  ATM_COVERAGE_FOOTNOTE_ID
                );
                if (!target) return;
                event.preventDefault();
                target.focus();
              }}
              sx={{
                color: "inherit",
                "&:hover, &:focus, &:visited, &:active, &.Mui-focusVisible": {
                  color: "inherit",
                },
                "&:focus": {
                  outline: "none",
                },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.focus}`,
                  outlineOffset: "2px",
                },
              }}
            >
              1
            </Link>
          </Box>
        </Typography>
      </Box>
      <Typography>
        {t__(
          theme.lang,
          countryDrawer,
          `atm.${hasAtmBanks ? "coverage" : "noCoverage"}.subtitle`
        )}
      </Typography>
      {hasAtmBanks && (
        <>
          <List
            style={{
              listStyle: "bullet",
              marginBottom: theme.spacing(5),
            }}
            className={
              countryData.atm_banks.length > 5 && fullScreen
                ? classes.longList
                : ""
            }
          >
            {countryData.atm_banks.map((a, ind) => {
              if (
                a !==
                "All banks displaying the Discover Network, Diners Club International and PULSE logos"
              ) {
                return (
                  <ListItem
                    dense={true}
                    key={`list${ind}`}
                    className={classes.orangeList}
                  >
                    <span>&nbsp;</span>
                    <b>{a}</b>
                  </ListItem>
                );
              } else {
                return null;
              }
            })}
          </List>
          {fixedFontSize ? (
            <Typography sx={{ fontSize: "1rem" }}>
              {t__(theme.lang, countryDrawer, "atm.coverage.supplmentary")}{" "}
              {t__(theme.lang, countryDrawer, "atm.coverage.fees")}
            </Typography>
          ) : (
            <>
              <Typography>
                {t__(theme.lang, countryDrawer, "atm.coverage.supplmentary")}
              </Typography>
              <Box mt={5}>
                <Typography>
                  {t__(theme.lang, countryDrawer, "atm.coverage.fees")}
                </Typography>
              </Box>
            </>
          )}
        </>
      )}
      <Typography
        id={ATM_COVERAGE_FOOTNOTE_ID}
        component="p"
        role="note"
        tabIndex={-1}
        sx={{
          fontSize: "0.8rem",
          marginTop: theme.spacing(5),
          outline: "none",
          "&:focus": {
            outline: "none",
          },
          "&:focus:not(:focus-visible)": {
            outline: "none",
          },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.focus}`,
            outlineOffset: "2px",
          },
        }}
      >
        1. {t__(theme.lang, countryDrawer, "atm.coverage.footnote")}
      </Typography>
    </StyledBox>
  );
};

AtmBox.propTypes = {
  countryData: PropTypes.object.isRequired,
  inDrawer: PropTypes.bool,
  /** When true, all text is 16px and fees copy is inline with supplementary; when false, fees is a separate paragraph */
  fixedFontSize: PropTypes.bool,
};

export default AtmBox;
